import {dsDtos} from '@vectronic/openApi-customer'
import {Buffer} from 'buffer'
import {isValidPhoneNumber} from 'libphonenumber-js'
import {Error} from './error'

export class Code {
  // eslint-disable-next-line sonarjs/cognitive-complexity
  static do(dto: dsDtos.Parameter, value?: string | null) {
    if (value == null) return this.handleNull(dto)
    const buffer = Buffer.alloc(dto.length!)
    const offset = dto.value === 'TIME_ZONE' ? -128 : (dto.offset ?? 0)
    const factor = dto.factor ?? 1
    let code: any
    let split: string[]
    switch (dto.value) {
      case 'TIME_ZONE':
      case 'UNSIGNED_INT':
      case 'TIME':
      case 'DAYTIME':
      case 'DATE_TIME':
      case 'RANGE':
      case 'TIME_SPAN':
      case 'MESSAGE_MODE':
      case 'FLAGS':
      case 'ENUMERATION':
      case 'BAND_FLAGS':
        code = this.toInt(value) / factor - offset
        buffer.writeUIntLE(code, 0, dto.length!)
        return buffer

      case 'SIGNED_INT':
        code = this.toInt(value) / factor - offset
        buffer.writeIntLE(code, 0, dto.length!)
        return buffer

      case 'PHONE':
      case 'STRING':
        if (dto.value === 'PHONE' && !isValidPhoneNumber(value)) throw Error.INVALID_PHONENUMBER
        buffer.write(value, 'utf8')
        return buffer

      case 'FLOAT':
        code = this.toFloat(value) / factor - offset
        buffer.writeFloatLE(code)
        return buffer

      case 'DATE':
        return this.date(value, buffer)

      case 'DATE_TIME_AND_UNSIGNED_INT':
        split = value.split(',')
        buffer.writeUInt32LE(this.toInt(split[0]))
        buffer.writeUIntLE(this.toInt(split[1]), 4, dto.length! - 4)
        return buffer

      case 'IP_ADDRESS':
      case 'VERSION_1_1_1_1':
        split = value.split('.')
        buffer.writeUInt8(this.toInt(split[0]), 3)
        buffer.writeUInt8(this.toInt(split[1]), 2)
        buffer.writeUInt8(this.toInt(split[2]), 1)
        buffer.writeUInt8(this.toInt(split[3]))
        return buffer

      case 'VERSION_2_2':
        split = value.split('.')
        buffer.writeUInt16LE(this.toInt(split[0]), 2)
        buffer.writeUInt16LE(this.toInt(split[1]))
        return buffer

      case 'VERSION_2_1_1':
        split = value.split('.')
        buffer.writeUInt16LE(this.toInt(split[0]), 2)
        buffer.writeUInt8(this.toInt(split[1]), 1)
        buffer.writeUInt8(this.toInt(split[2]))
        return buffer

      case 'DATE_AND_VERSION_1_1_1':
        split = value.split(',')
        this.date(split[0], buffer)
        const versionSplit = split[1].split('.')
        buffer.writeUInt8(this.toInt(versionSplit[2]), 3)
        buffer.writeUInt8(this.toInt(versionSplit[1]), 4)
        buffer.writeUInt8(this.toInt(versionSplit[0]), 5)
        return buffer

      case 'HEX':
        buffer.write(value, 'hex')
        return buffer

      case 'HEX_DOT':
        code = value.split('.').join('')
        buffer.write(code, 'hex')
        return buffer

      case 'VHF_PATTERN':
        split = value.split(',')
        buffer.writeUInt8(this.toInt(split[0]))
        buffer.writeUInt8(this.toInt(split[1]), 1)
        buffer.writeUInt8(this.toInt(split[2]) / 50, 2)
        return buffer

      case 'CALIBRATION_BIAS':
        throw `${Error.NOT_IMPLEMENTED}: CALIBRATION_BIAS` //TODO

      case 'CALIBRATION_MATRIX':
        throw `${Error.NOT_IMPLEMENTED}: CALIBRATION_MATRIX.` //TODO

      case 'LIST_UNSIGNED_INT_4':
        split = value.split(',')
        for (let i = 0; i < split.length; i++) buffer.writeUInt32LE(this.toInt(split[i]), i * 4)
        for (let i = split.length * 4; i < buffer.length; i++) buffer[i] = 0xff
        return buffer

      case 'LIST_UNSIGNED_INT_2':
        split = value.split(',')
        for (let i = 0; i < split.length; i++) buffer.writeUInt16LE(this.toInt(split[i]), i * 2)
        for (let i = split.length * 2; i < buffer.length; i++) buffer[i] = 0xff
        return buffer

      case 'LIST_DATE_TIME_AND_HEX_8':
        split = value.split(',')
        let bufferOffset = 0
        for (let i = 0; i < split.length; i++) {
          buffer.writeUInt32LE(this.toInt(split[i++]), bufferOffset)
          bufferOffset += 4
          buffer.writeUInt32BE(this.toInt(split[i], 16), bufferOffset)
          bufferOffset += 4
        }
        for (let i = split.length * 8; i < buffer.length; i++) buffer[i] = 0xff
        return buffer

      case 'GLOBALSTAR_ESN':
        split = value.split('-')
        buffer.writeUIntLE(this.toInt(split[1]), 0, 3)
        buffer.writeUInt8(this.toInt(split[0]), 3)
        return buffer
    }
  }

  private static toFloat(s: string) {
    if (!/^[-.+e\d]+$/.test(s)) throw Error.NAN
    const float = parseFloat(s)
    if (isNaN(float)) throw Error.NAN
    return float
  }

  private static toInt(s: string, radix?: number) {
    const float = this.toFloat(s)
    if (!Number.isInteger(float)) throw Error.NOT_A_INT
    return parseInt(s, radix)
  }

  private static date(data: string, buffer: Buffer) {
    const milliSecondsInADay = 1000 * 60 * 60 * 24
    const daysSince2000 = this.toInt(data)
    const date = new Date(Date.UTC(2000, 0) + daysSince2000 * milliSecondsInADay)
    buffer.writeUInt8(date.getUTCFullYear() - 2000, 0)
    buffer.writeUInt8(date.getUTCMonth() + 1, 1)
    buffer.writeUInt8(date.getUTCDate(), 2)
    return buffer
  }

  private static handleNull(dto: dsDtos.Parameter) {
    if (dto.value === 'FLAGS') throw Error.INVALID_INPUT
    if (dto.value === 'SIGNED_INT') throw Error.INVALID_INPUT
    const buffer = Buffer.alloc(dto.length!)
    buffer.fill(255)
    return buffer
  }
}
